import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { mutate } from "swr";
import { Badge } from "~/components/ui/badge";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentConnector,
  AgentScope,
  Connector,
  ConnectorSource,
  agentConnectorsPath,
  connectorsPath,
  disableAgentConnector,
  enableAgentConnector,
} from "~/services/agents";
import { AgentTabHeader } from "./AgentTabHeader";

export const AgentConnectorsTab = () => {
  const agent = useOutletContext<Agent>();
  const canEdit = useCanEditAgent(agent);
  const { showSuccess, showError } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const { data: connectors, isLoading: connectorsLoading } =
    useFetcher<Connector[]>(connectorsPath());
  const { data: agentConnectors, isLoading: agentConnectorsLoading } = useFetcher<AgentConnector[]>(
    agentConnectorsPath(agent.id)
  );

  const enabledIds = new Set((agentConnectors ?? []).map((c) => c.connector.id));
  const isLoading = connectorsLoading || agentConnectorsLoading;
  const totalCount = connectors?.length ?? 0;

  const handleToggle = async (connector: Connector, enabled: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(connector.id));
    try {
      if (enabled) {
        await enableAgentConnector(agent.id, connector.id);
        showSuccess(`"${connector.name}" Connector enabled`);
      } else {
        await disableAgentConnector(agent.id, connector.id);
        showSuccess(`"${connector.name}" Connector disabled`);
      }
      await mutate(agentConnectorsPath(agent.id));
    } catch {
      showError(`Failed to ${enabled ? "enable" : "disable"} "${connector.name}" Connector`);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(connector.id);
        return next;
      });
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <AgentTabHeader
        title="Connectors"
        isSystemAgent={agent.scope === AgentScope.SYSTEM}
        count={isLoading || totalCount === 0 ? undefined : enabledIds.size}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        context={isLoading ? undefined : "External MCP servers the agent can reach"}
        description="Connectors expose tools hosted on external MCP servers. Attach the ones this agent needs to do its job."
      />

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : !connectors?.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No connectors available</p>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {connectors.map((connector) => (
            <div key={connector.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium">{connector.name}</p>
                  {connector.source === ConnectorSource.SYSTEM && (
                    <Badge variant="outline" className="rounded-full text-xs">
                      System
                    </Badge>
                  )}
                </div>
                {connector.description && (
                  <p className="text-xs text-muted-foreground">{connector.description}</p>
                )}
                <p className="truncate text-xs text-muted-foreground/80" title={connector.url}>
                  {connector.url}
                </p>
              </div>
              {togglingIds.has(connector.id) ? (
                <div className="flex h-[1.15rem] w-8 items-center justify-center">
                  <Loader2 className="size-3 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Switch
                  checked={enabledIds.has(connector.id)}
                  onCheckedChange={(checked) => handleToggle(connector, checked)}
                  disabled={!canEdit}
                  aria-label={`${enabledIds.has(connector.id) ? "Disable" : "Enable"} ${connector.name}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
